import React, { useState } from 'react';
import './FeaturedTab.css';
import useCatalogManagementStore from '@/stores/AdminStore/CatalogManagementStore';
import { FeaturedCourse } from '@/services/PagesServices/CatalogManagementService';

const FeaturedTab: React.FC = () => {
  const { featuredCourses, recommendationRules, addFeaturedCourse, toggleFeaturedCourseActive } = useCatalogManagementStore();

  const [featuredModalOpen, setFeaturedModalOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);

  const [featuredFormData, setFeaturedFormData] = useState({
    title: '',
    category: 'All Categories',
  });

  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    condition: '',
    courses: [] as string[],
    courseInput: '',
  });

  const openFeaturedModal = () => {
    setFeaturedModalOpen(true);
    setFeaturedFormData({ title: '', category: 'All Categories' });
  };

  const closeFeaturedModal = () => {
    setFeaturedModalOpen(false);
    setFeaturedFormData({ title: '', category: 'All Categories' });
  };

  const handleFeaturedSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newCourse: FeaturedCourse = {
      id: Date.now().toString(),
      title: featuredFormData.title,
      category: featuredFormData.category,
      rating: 4.5,
      enrolled: 0,
      isActive: true,
    };
    addFeaturedCourse(newCourse);
    closeFeaturedModal();
  };

  const openRuleModal = () => {
    setRuleModalOpen(true);
    setRuleFormData({ name: '', condition: '', courses: [], courseInput: '' });
  };

  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setRuleFormData({ name: '', condition: '', courses: [], courseInput: '' });
  };

  const handleRuleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    closeRuleModal();
  };

  const addCourseToRule = () => {
    if (ruleFormData.courseInput.trim()) {
      setRuleFormData({
        ...ruleFormData,
        courses: [...ruleFormData.courses, ruleFormData.courseInput.trim()],
        courseInput: '',
      });
    }
  };

  const handleCourseInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCourseToRule();
    }
  };

  const toggleCourseActive = (courseId: string) => {
    toggleFeaturedCourseActive(courseId);
  };

  return (
    <div className="admin-catalog-featured-content">
      <div className="admin-catalog-featured-section">
        <div className="admin-catalog-section-header">
          <div className="admin-catalog-section-title-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="admin-catalog-section-icon">
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="admin-catalog-section-title">Featured Courses</h2>
          </div>
          <button className="admin-catalog-add-featured-button" onClick={openFeaturedModal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Featured Course
          </button>
        </div>

        <div className="admin-catalog-featured-courses-list">
          {featuredCourses?.map((course) => (
            <div key={course.id} className="admin-catalog-featured-course-item">
              <div className="admin-catalog-drag-handle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M5 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <div className="admin-catalog-course-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className="admin-catalog-course-info">
                <h3 className="admin-catalog-course-title">{course.title}</h3>
                <div className="admin-catalog-course-meta">
                  <span className="admin-catalog-course-category">{course.category}</span>
                  <div className="admin-catalog-course-rating">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span>{course.rating}</span>
                  </div>
                  <span className="admin-catalog-course-enrolled">{course.enrolled} enrolled</span>
                </div>
              </div>

              <div className="admin-catalog-course-actions">
                <button className="admin-catalog-action-button" aria-label="Edit">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button className="admin-catalog-action-button" aria-label="Delete">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <label className="admin-catalog-toggle-switch">
                  <input
                    type="checkbox"
                    checked={course.isActive}
                    onChange={() => toggleCourseActive(course.id)}
                  />
                  <span className="admin-catalog-toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-catalog-recommendation-section">
        <div className="admin-catalog-section-header">
          <div className="admin-catalog-section-title-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="admin-catalog-section-icon">
              <path
                d="M22 12h-4l-3 9L9 3l-3 9H2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="admin-catalog-section-title">Recommendation Rules</h2>
          </div>
          <button className="admin-catalog-add-rule-button" onClick={openRuleModal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Rule
          </button>
        </div>

        <div className="admin-catalog-rules-list">
          {recommendationRules?.map((rule) => (
            <div key={rule.id} className="admin-catalog-rule-item">
              <div className="admin-catalog-rule-header">
                <div className="admin-catalog-rule-title-row">
                  <h3 className="admin-catalog-rule-name">{rule.name}</h3>
                  <span className={`admin-catalog-status-badge admin-catalog-${rule.status.toLowerCase()}`}>
                    {rule.status}
                  </span>
                </div>
                <div className="admin-catalog-rule-actions">
                  <button className="admin-catalog-action-button" aria-label="Edit">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button className="admin-catalog-action-button" aria-label="Delete">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="admin-catalog-rule-description">{rule.description}</p>
              <div className="admin-catalog-rule-tags">
                {rule.tags.map((tag, index) => (
                  <span key={index} className="admin-catalog-rule-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Featured Course Modal */}
      {featuredModalOpen && (
        <div className="admin-catalog-modal-overlay" onClick={closeFeaturedModal}>
          <div className="admin-catalog-modal-content admin-catalog-featured-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-catalog-modal-header">
              <div className="admin-catalog-modal-header-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="admin-catalog-modal-title">Add Featured Course</h2>
              <button className="admin-catalog-modal-close" onClick={closeFeaturedModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleFeaturedSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="courseTitle" className="admin-catalog-form-label">
                  Course Title <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="courseTitle"
                  className="admin-catalog-form-input"
                  placeholder="Enter course title"
                  value={featuredFormData.title}
                  onChange={(e) => setFeaturedFormData({ ...featuredFormData, title: e.target.value })}
                  required
                />
              </div>

              <div className="admin-catalog-form-group">
                <label htmlFor="courseCategory" className="admin-catalog-form-label">
                  Category
                </label>
                <select
                  id="courseCategory"
                  className="admin-catalog-form-select"
                  value={featuredFormData.category}
                  onChange={(e) => setFeaturedFormData({ ...featuredFormData, category: e.target.value })}
                >
                  <option>All Categories</option>
                  <option>Leadership & Management</option>
                  <option>Technical Skills</option>
                  <option>Soft Skills</option>
                  <option>Compliance & Safety</option>
                </select>
              </div>

              <div className="admin-catalog-info-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8h.01M12 12v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <div>
                  <strong>Featured Course Settings</strong>
                  <p>Featured courses will be displayed prominently on the learner catalog homepage. Use the order controls to arrange them.</p>
                </div>
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeFeaturedModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Recommendation Rule Modal */}
      {ruleModalOpen && (
        <div className="admin-catalog-modal-overlay" onClick={closeRuleModal}>
          <div className="admin-catalog-modal-content admin-catalog-rule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-catalog-modal-header">
              <div className="admin-catalog-modal-header-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 12h-4l-3 9L9 3l-3 9H2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="admin-catalog-modal-title">Create Recommendation Rule</h2>
              <button className="admin-catalog-modal-close" onClick={closeRuleModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleRuleSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="ruleName" className="admin-catalog-form-label">
                  Rule Name <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="ruleName"
                  className="admin-catalog-form-input"
                  placeholder="e.g., New Joiners Onboarding"
                  value={ruleFormData.name}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, name: e.target.value })}
                  required
                />
              </div>

              <div className="admin-catalog-form-group">
                <label htmlFor="ruleCondition" className="admin-catalog-form-label">
                  Condition <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="ruleCondition"
                  className="admin-catalog-form-input"
                  placeholder="e.g., Show to employees with tenure < 30 days"
                  value={ruleFormData.condition}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, condition: e.target.value })}
                  required
                />
                <p className="admin-catalog-form-hint">Describe when this rule should trigger. This is for documentation purposes.</p>
              </div>

              <div className="admin-catalog-form-group">
                <label htmlFor="recommendedCourses" className="admin-catalog-form-label">
                  Recommended Courses <span className="admin-catalog-required">*</span>
                </label>
                <div className="admin-catalog-course-input-wrapper">
                  <input
                    type="text"
                    id="recommendedCourses"
                    className="admin-catalog-form-input"
                    placeholder="Enter course name and press Enter"
                    value={ruleFormData.courseInput}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, courseInput: e.target.value })}
                    onKeyPress={handleCourseInputKeyPress}
                  />
                  <button type="button" className="admin-catalog-add-course-btn" onClick={addCourseToRule}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Add
                  </button>
                </div>
                {ruleFormData.courses.length > 0 && (
                  <div className="admin-catalog-added-courses">
                    {ruleFormData.courses.map((course, index) => (
                      <span key={index} className="admin-catalog-course-tag">
                        {course}
                        <button
                          type="button"
                          onClick={() => {
                            setRuleFormData({
                              ...ruleFormData,
                              courses: ruleFormData.courses.filter((_, i) => i !== index),
                            });
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-catalog-smart-recommendations-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <strong>Smart Recommendations</strong>
                  <p>Recommendation rules automatically suggest relevant courses to learners based on conditions like role, department, tenure, or completion history.</p>
                </div>
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeRuleModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Create Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedTab;
